// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — this browser's view of the signed-in account and its AYNI XR
 * licence (`docs/AYNI_XR_LICENSING.md`).
 *
 * The account lives on the site's server (`functions/api/account/`): this
 * module asks it who is signed in and what the licence allows, and sends
 * the sign-in, sign-out, licence and deletion requests. The session is an
 * HttpOnly cookie the page never sees, and the licence's signed token never
 * reaches the browser — only its summary. Free use needs no account.
 */

import { entitlementsFrom, personalAllowances, withPersonalAllowances, XR_PLANS, type XrEntitlements, type XrPlanId } from './xrPlans'

export type LicenceError =
  | 'invalid_key' | 'wrong_product' | 'redeem_expired' | 'transfer_limit' | 'seat_taken'
  | 'license_suspended' | 'license_revoked' | 'license_expired' | 'unreachable' | 'unknown'
  | 'too_many_licences'

export interface LicenceSummary {
  status: 'active' | 'lease-expired' | 'expired'
  plan: XrPlanId | null
  holder: string
  hint: string
  /** Paid-until, seconds. */
  paidUntil: number
  /** When the licence itself ends, seconds; absent from a server older than the capability keys. */
  licenceEnd?: number
  packages: string[]
  /** The capability keys (`xr-cap-*`) still current on the licence; absent from an older server. */
  caps?: string[]
  /** AYNI (several licences, 2026-10-10): the licence's reference, to choose or remove it; absent from an older server. */
  ref?: string
  /** Whether it is the licence in use. */
  inUse?: boolean
}

/** Why the licence in use changed without its holder choosing (decision M6), until it has been seen. */
export interface LicenceNotice {
  reason: LicenceError | 'ended'
  fromPlan: XrPlanId | null
  fromHint: string
  toPlan: XrPlanId | null
  at: string
}

/**
 * AYNI: the account's subscription with Paddle, as the site's server
 * mirrors it (`functions/api/billing/_billing-lib.ts` `billingSummary`).
 */
export interface BillingSummary {
  status: 'active' | 'trialing' | 'past_due' | 'paused' | 'canceled'
  /** The plan bought: plus, teacher, school, researcher or lab. */
  plan: string
  /** Licence package features (`*` for all, none for an Education plan without add-ons); null when not known. */
  packages: string[] | null
  seats: number
  interval: 'month' | 'year'
  /** End of the period paid for (ISO). */
  periodEndsAt: string | null
  /** A cancel or pause asked for, and when it takes effect (ISO). */
  scheduledAction: string | null
  scheduledAt: string | null
  pastDueSince: string | null
  endedAt: string | null
  /** When the plan stops unless a payment arrives (past due), ISO. */
  graceEndsAt: string | null
  /** Refunded (a withdrawal) or charged back: the licence was revoked then (owner, 2026-10-07). */
  revokedAt: string | null
  revokedReason: string | null
}

export interface AccountState {
  status: 'unknown' | 'signedOut' | 'signedIn'
  email: string | null
  name: string
  /** The licence in use (decision M1), or none. */
  licence: LicenceSummary | null
  /** Every licence on the account, the one in use first (several since 2026-10-10). */
  licences: LicenceSummary[]
  /** How many licences the account may hold (1 where the site is not set up for more). */
  maxLicences: number
  /** Why the licence in use changed by itself, until seen (decision M6). */
  notice: LicenceNotice | null
  /** Why the licence server last took the account's licence away. */
  refused: LicenceError | null
  /** AYNI: the account's Paddle subscription, when it has one; absent from an older server. */
  billing?: BillingSummary | null
  /** The ways this site offers to sign in. */
  signIn: { email: boolean; google: boolean }
  /** AYNI: whether own data is set up on this site (My data, Passkeys). */
  ownData: boolean
}

export type SignInError =
  | 'invalid_email' | 'too_many' | 'mail_unavailable' | 'mail_failed' | 'unreachable' | 'challenge_failed'
  | 'link_invalid' | 'link_expired' | 'link_used'
  | 'google_denied' | 'google_state' | 'google_failed' | 'google_unavailable'

/** Where "Continue with Google" goes. */
export const GOOGLE_SIGN_IN = '/api/account/google/start'

const SIGNED_OUT: AccountState = {
  status: 'signedOut', email: null, name: '', licence: null, licences: [], maxLicences: 1, notice: null, refused: null, signIn: { email: false, google: false }, ownData: false,
}
let state: AccountState = { ...SIGNED_OUT, status: 'unknown' }
const listeners = new Set<(state: AccountState) => void>()

export function accountState(): AccountState {
  return state
}

/**
 * AYNI: resolves once the account is known (signed in or out), or after
 * `timeoutMs` whatever it is: for what must not run on Free's limits
 * while a paid account is still being read.
 */
export function accountKnown(timeoutMs: number): Promise<void> {
  if (state.status !== 'unknown') return Promise.resolve()
  return new Promise((resolve) => {
    const off = onAccountChange((s) => {
      if (s.status !== 'unknown') finish()
    })
    const timer = setTimeout(finish, timeoutMs)
    function finish(): void {
      clearTimeout(timer)
      off()
      resolve()
    }
  })
}

/** Called now and on every change. Returns an unsubscribe. */
export function onAccountChange(listener: (state: AccountState) => void): () => void {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

function set(next: AccountState): AccountState {
  state = next
  for (const listener of listeners) listener(state)
  return state
}

async function call(method: string, path: string, body: unknown, fetchImpl: typeof fetch): Promise<{ status: number; body: Record<string, unknown> } | null> {
  try {
    const res = await fetchImpl(path, {
      method,
      headers: body === undefined ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    })
    return { status: res.status, body: (await res.json().catch(() => ({}))) as Record<string, unknown> }
  } catch {
    return null
  }
}

/** Ask the site who is signed in. */
export async function loadAccount(fetchImpl: typeof fetch = fetch): Promise<AccountState> {
  const reply = await call('GET', '/api/account', undefined, fetchImpl)
  if (!reply || reply.status !== 200) return set({ ...SIGNED_OUT })
  const b = reply.body as Partial<{
    signedIn: boolean; account: { email: string; name: string }; licence: LicenceSummary | null; licences: LicenceSummary[]; maxLicences: number
    notice: LicenceNotice | null; refused: LicenceError | null; billing: BillingSummary | null; signIn: { email: boolean; google: boolean }; ownData: boolean
  }>
  const signIn = { email: b.signIn?.email === true, google: b.signIn?.google === true }
  const ownData = b.ownData === true
  if (!b.signedIn || !b.account) return set({ ...SIGNED_OUT, signIn, ownData })
  const licence = b.licence ?? null
  return set({
    status: 'signedIn', email: b.account.email, name: b.account.name ?? '', licence,
    // A server older than several licences sends the one licence only.
    licences: Array.isArray(b.licences) ? b.licences : licence ? [licence] : [],
    maxLicences: typeof b.maxLicences === 'number' ? b.maxLicences : 1,
    notice: b.notice ?? null, refused: b.refused ?? null, billing: b.billing ?? null, signIn, ownData,
  })
}

const entitlementsOf = (licence: LicenceSummary | null): XrEntitlements => {
  const plan = licence && licence.status === 'active' && licence.plan ? XR_PLANS[licence.plan] : null
  return entitlementsFrom(plan, plan ? licence!.packages : [], plan ? licence!.caps ?? [] : [])
}

/**
 * What this browser may do now: the licence in use's, or the free set; the
 * chatbot and Khipu keeping are the best of every licence the account holds
 * (decision M1), as the server judges them.
 */
export function currentEntitlements(): XrEntitlements {
  if (state.status !== 'signedIn') return entitlementsFrom(null)
  const inUse = entitlementsOf(state.licence)
  if (state.licences.length < 2) return inUse
  const held = state.licences.map((l) => ({ entitlements: entitlementsOf(l), endMs: (l.licenceEnd ?? l.paidUntil) * 1000 }))
  return withPersonalAllowances(inUse, personalAllowances(held, Date.now()))
}

/** The licences an account could switch to now: current, and not the one in use. */
export function otherCurrentLicences(account: AccountState = state): LicenceSummary[] {
  return account.licences.filter((l) => !l.inUse && l.status === 'active' && !!l.ref)
}

/**
 * AYNI (decision M4): whether the licence in use may change now; not while
 * this browser presents a meeting (main.ts says so). The server cannot see
 * a meeting, so the page keeps this rule.
 */
let switchBlocked: () => boolean = () => false
export function setLicenceSwitchBlocked(check: () => boolean): void {
  switchBlocked = check
}
export function licenceSwitchBlocked(): boolean {
  return switchBlocked()
}

const signInErrorOf = (body: Record<string, unknown>): SignInError => {
  const known: readonly SignInError[] = ['invalid_email', 'too_many', 'mail_unavailable', 'mail_failed', 'challenge_failed', 'link_invalid', 'link_expired', 'link_used']
  return known.includes(body.error as SignInError) ? (body.error as SignInError) : 'mail_failed'
}

/**
 * Email a sign-in link. A local server may hand it back instead (`devLink`).
 * `turnstileToken` is the form's Turnstile token when the build has a site
 * key (`src/services/turnstile.ts`); the server refuses without one when
 * its secret is set (`challenge_failed`).
 */
export async function requestSignInLink(email: string, fetchImpl: typeof fetch = fetch, turnstileToken?: string | null): Promise<{ ok: true; devLink?: string } | { ok: false; error: SignInError }> {
  const reply = await call('POST', '/api/account/email', { email: email.trim(), ...(turnstileToken ? { turnstileToken } : {}) }, fetchImpl)
  if (!reply) return { ok: false, error: 'unreachable' }
  if (reply.status === 200) return { ok: true, ...(typeof reply.body.devLink === 'string' ? { devLink: reply.body.devLink } : {}) }
  return { ok: false, error: signInErrorOf(reply.body) }
}

/** Finish signing in with a link's secret. */
export async function completeSignIn(token: string, fetchImpl: typeof fetch = fetch): Promise<{ ok: true } | { ok: false; error: SignInError }> {
  const reply = await call('POST', '/api/account/verify', { token }, fetchImpl)
  if (!reply) return { ok: false, error: 'unreachable' }
  if (reply.status !== 200) {
    const error = signInErrorOf(reply.body)
    return { ok: false, error: error.startsWith('link_') ? error : 'link_invalid' }
  }
  await loadAccount(fetchImpl)
  return { ok: true }
}

/** Where the welcome page keeps a deep link for after the sign-in (public/welcome.js). */
export const RETURN_KEY = 'ayni-return-to'

/**
 * AYNI: the deep link a visitor opened before signing in (`/dataset/…`,
 * `?room=…`), kept by the welcome page, taken once. Only a same-site path:
 * anything else is dropped.
 */
export function takeReturnTo(storage: Pick<Storage, 'getItem' | 'removeItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): string | null {
  if (!storage) return null
  try {
    const kept = storage.getItem(RETURN_KEY)
    const until = Number(storage.getItem(`${RETURN_KEY}-until`))
    storage.removeItem(RETURN_KEY)
    storage.removeItem(`${RETURN_KEY}-until`)
    // A kept link lasts an hour (review L-4): it may hold a share link's secret.
    if (until && until < Date.now()) return null
    return kept && kept.startsWith('/') && !kept.startsWith('//') && kept !== '/' ? kept : null
  } catch {
    return null
  }
}

export async function signOut(fetchImpl: typeof fetch = fetch): Promise<void> {
  await call('POST', '/api/account/signout', {}, fetchImpl)
  set({ ...SIGNED_OUT, signIn: state.signIn })
}

/**
 * Delete the account; `confirm` is its email, typed by its owner. `step_up`:
 * confirm it is you first (`myDataStepUp.ts`), then call again. AYNI:
 * 'subscription' while a Paddle subscription still runs (cancel it first).
 */
export async function deleteAccount(confirm: string, fetchImpl: typeof fetch = fetch): Promise<'deleted' | 'mismatch' | 'subscription' | 'step_up' | 'failed'> {
  const reply = await call('POST', '/api/account/delete', { confirm }, fetchImpl)
  if (reply?.status === 200) {
    set({ ...SIGNED_OUT, signIn: state.signIn })
    return 'deleted'
  }
  if (reply?.status === 409 && reply.body.error === 'subscription_active') return 'subscription'
  // AYNI (review M-1): deleting the account needs a step-up (a passkey, or an emailed code) first.
  if (reply?.status === 403 && reply.body.error === 'step_up_required') return 'step_up'
  return reply?.status === 400 ? 'mismatch' : 'failed'
}

const LICENCE_ERRORS: readonly LicenceError[] = [
  'invalid_key', 'wrong_product', 'redeem_expired', 'transfer_limit', 'seat_taken',
  'license_suspended', 'license_revoked', 'license_expired', 'unreachable', 'too_many_licences',
]

/** The account's licences as a licence route answered them. */
function withLicences(body: Record<string, unknown>): AccountState {
  const licences = Array.isArray(body.licences) ? (body.licences as LicenceSummary[]) : []
  const licence = (body.licence as LicenceSummary | null | undefined) ?? null
  return { ...state, licence, licences: licences.length > 0 || !licence ? licences : [licence], refused: null }
}

/**
 * Put a licence key on the account. With several licences the new one is
 * in use unless `use` is false, or this browser presents a meeting (M4:
 * the licence in use stays). `already`: the account held it.
 */
export async function addLicenceKey(key: string, fetchImpl: typeof fetch = fetch, use = true): Promise<{ ok: true; already: boolean } | { ok: false; error: LicenceError | 'signed_out' }> {
  const reply = await call('POST', '/api/account/licence', { key: key.trim(), use: use && !licenceSwitchBlocked() }, fetchImpl)
  if (!reply) return { ok: false, error: 'unreachable' }
  if (reply.status === 401) {
    set({ ...SIGNED_OUT, signIn: state.signIn })
    return { ok: false, error: 'signed_out' }
  }
  if (reply.status !== 200) {
    return { ok: false, error: LICENCE_ERRORS.includes(reply.body.error as LicenceError) ? (reply.body.error as LicenceError) : 'unknown' }
  }
  set(withLicences(reply.body))
  return { ok: true, already: reply.body.already === true }
}

/**
 * Take a licence off the account (`ref`; the one in use without it), giving
 * its seat back. Not the one in use while this browser presents a meeting
 * (M4): that would change the licence in use.
 */
export async function removeLicenceKey(fetchImpl: typeof fetch = fetch, ref?: string): Promise<'ok' | 'blocked'> {
  if (licenceSwitchBlocked() && (!ref || state.licence?.ref === ref)) return 'blocked'
  const reply = await call('DELETE', ref ? `/api/account/licence?ref=${encodeURIComponent(ref)}` : '/api/account/licence', undefined, fetchImpl)
  if (reply?.status === 200) set(withLicences(reply.body))
  else await loadAccount(fetchImpl)
  return 'ok'
}

/**
 * Work with another of the account's licences (decision M1). Everything on
 * screen stays; what the new plan does not cover closes (main.ts, on the
 * account change). Refused while this browser presents a meeting (M4).
 */
export async function useLicence(ref: string, fetchImpl: typeof fetch = fetch): Promise<'ok' | 'blocked' | 'not_current' | 'failed'> {
  if (licenceSwitchBlocked()) return 'blocked'
  const reply = await call('PATCH', '/api/account/licence', { use: ref }, fetchImpl)
  if (reply?.status === 200) {
    set({ ...withLicences(reply.body), notice: null })
    return 'ok'
  }
  if (reply?.status === 409) return 'not_current'
  await loadAccount(fetchImpl)
  return 'failed'
}

/** The notice (decision M6) has been seen. */
export async function dismissLicenceNotice(fetchImpl: typeof fetch = fetch): Promise<void> {
  set({ ...state, notice: null })
  await call('PATCH', '/api/account/licence', { noticeSeen: true }, fetchImpl)
}

/**
 * The sign-in parts of an address: an emailed link's secret (`?signin=`), a
 * finished Google sign-in (`?signedIn=google`) or why it failed
 * (`?signInError=`). Removes them from the address bar, so a link's secret
 * is not left in history or shared by copying the address.
 */
export function takeSignInParams(win: Pick<Window, 'location' | 'history'> | null = typeof window === 'undefined' ? null : window): { token?: string; google?: boolean; error?: SignInError } {
  if (!win) return {}
  const url = new URL(win.location.href)
  const token = url.searchParams.get('signin') ?? undefined
  const google = url.searchParams.get('signedIn') === 'google'
  const error = (url.searchParams.get('signInError') ?? undefined) as SignInError | undefined
  if (!token && !google && !error) return {}
  for (const name of ['signin', 'signedIn', 'signInError']) url.searchParams.delete(name)
  try {
    win.history.replaceState(win.history.state, '', url.pathname + url.search + url.hash)
  } catch {
    // A sandboxed page: the parameters stay; nothing else depends on it.
  }
  return { ...(token ? { token } : {}), ...(google ? { google } : {}), ...(error ? { error } : {}) }
}
